/* eslint-disable */
import React from 'react'
import { FormattedMessage } from 'react-intl'

import SortingArrow from 'components/SortingArrow'
import sortDirection from 'constants/sortDirection'
import OptionIcon from 'assets/icons/options-icon'
import ExternalLinkIcon from 'assets/icons/external-link-icon'
import { ReportsFactsheetIconWrapper, ReportsFactsheetWrapper, ReportsIucnStatusWrapper } from '../style'
import { SortingArrowWrapper } from 'components/SortingArrow/style'
import { getStickyTableColumnsLength } from 'utils/getStickyTableColumnsLength'

export const renderReportsIucnStatusIcon = (status) => {
  const color = {
    EX: '#FFFFFF',
    EW: 'rgba(84, 35, 68, 0.3)',
    CR: 'rgba(216, 30, 5, 0.5)',
    EN: 'rgba(252, 127, 63, 0.6)',
    VU: 'rgba(249, 232, 20, 0.6)',
    LR: 'rgba(226, 211, 105, 0.8)',
    NT: 'rgba(204, 226, 38, 0.6)',
    LC: 'rgba(96, 198, 89, 0.6)',
    DD: '#D1D1C6'
  }

  return (
    <ReportsIucnStatusWrapper color={color[status]}>
      <FormattedMessage id={status} />
    </ReportsIucnStatusWrapper>
  )
}

export const CSV_HEADER_MAP = {
  siteCode: 'Site code',
  siteNameEng: 'Site name',
  monitorYear: 'Monitoring year',
  monitoringStatusPlain: 'Evaluation status',
  stateEvaluation: 'State completed',
  pressureEvaluation: 'Pressure completed',
  responseEvaluation: 'Response completed',

  stateBest: 'Best state result',
  stateWorst: 'Worst state result',
  stateMedian: 'Median state result',
  numberTriggers: 'Triggers monitored',

  pressureBest: 'Best threat result',
  pressureWorst: 'Worst threat result',
  pressureMedian: 'Median threat result',
  pressureTotal: 'Total threats documented',

  coveragePa: 'Coverage by PA',
  coverageOecm: 'Coverage by OECMs',
  coveragePaOecm: 'Coverage by PA & OECMs',

  managementPlanning: 'Management planning',
  conservationAction: 'Conservation action',

  // actionsNeededUnderwayCount: 'Actions needed & underway',
  actionsNeededCount: 'Actions needed',
  actionsUnderway: 'Actions underway'
}

export const normalizeMonitoringSitesData = (tableData = []) => {
  if (!Array.isArray(tableData)) return []

  return tableData.map((site) => {
    const statusText = site.monitoring_status ?? '-'
    const capitalizedStatus = statusText === '-' ? '-' : statusText.charAt(0).toUpperCase() + statusText.slice(1)

    const status = statusText.toLowerCase()
    const dotColor = status === 'draft' ? '#FF0000' : status === 'confirmed' || status === 'completed' ? 'green' : 'transparent' 
    return {
      siteCode: site.siteCode,
      siteNameEng: site.name || '',
      monitorYear: site?.monitor_year ?? '-',
      monitoringStatus: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: dotColor,
              display: 'inline-block'
            }}
          />
          <span>{capitalizedStatus}</span>
        </div>
      ),
      monitoringStatusPlain: capitalizedStatus,
      stateEvaluation: site.state_evaluation ? 'Yes' : 'No',
      pressureEvaluation: site.pressure_evaluation ? 'Yes' : 'No',
      responseEvaluation: site.response_evaluation ? 'Yes' : 'No',
      stateBest: site.state_result_best ?? '-',
      stateWorst: site.state_result_worst ?? '-',
      stateMedian: site.state_result_median ?? '-',
      numberTriggers: site.number_triggers ?? '0',
      pressureBest: site.pressure_result_best ?? '-',
      pressureWorst: site.pressure_result_worst ?? '-',
      pressureMedian: site.pressure_result_median ?? '-',
      pressureTotal: site.pressure_total_number ?? 0,
      coveragePa: site.designation_pa_id ?? '-',
      coverageOecm: site.designation_oecms_id ?? '-',
      coveragePaOecm: site.designation_pa_oecms_id ?? '-',
      managementPlanning: site.response_planning_id ?? '-',
      conservationAction: site.response_action_id ?? '-',
      actionsNeededCount: site.actions_needed_count ?? '-',
      actionsUnderway: site.actions_underway_count ?? '-',
      options: (
        <ReportsFactsheetWrapper>
          <ReportsFactsheetIconWrapper>
            <ExternalLinkIcon />
          </ReportsFactsheetIconWrapper>
          <ReportsFactsheetIconWrapper>
            <OptionIcon />
          </ReportsFactsheetIconWrapper>
        </ReportsFactsheetWrapper>
      )
    }
  })
}

export const makeMonitoringSitesTabColumns = (sorting, setSorting, rows = []) => {
  const safeRows = Array.isArray(rows) ? rows : []
    const sortFunction = (sortBy) => {
      if (sorting.sortBy === sortBy) {
        if (sorting.sortDirection === sortDirection.ASC) {
          setSorting({ sortBy: sortBy, sortDirection: sortDirection.DESC })
        } else {
          setSorting({ sortBy: sortBy, sortDirection: sortDirection.ASC })
        }
      } else {
        setSorting({ sortBy: sortBy, sortDirection: sortDirection.ASC })
      }
    }
    const sortBySiteId = () => {
      sortFunction('siteCode')
    }
    const sortBysiteNameEng = () => {
      sortFunction('name')
    }
    return [
      {
        id: 'siteCode',
        header: (
          <SortingArrowWrapper>
            <FormattedMessage id="siteCode" />
            <SortingArrow
              onChange={sortBySiteId}
              checked={sorting.sortBy === 'siteCode' && sorting.sortDirection === sortDirection.ASC}
            />
          </SortingArrowWrapper>
        ),
        accessorKey: 'siteCode',
        width: 100
      },
      {
        id: 'siteNameEng',
        header: (
          <SortingArrowWrapper>
            <FormattedMessage id="siteNameEng" />
            <SortingArrow
              onChange={sortBysiteNameEng}
              checked={sorting.sortBy === 'name' && sorting.sortDirection === sortDirection.ASC}
            />
          </SortingArrowWrapper>
        ),
        accessorKey: 'siteNameEng',
        width: getStickyTableColumnsLength('siteNameEng', safeRows, 250)
      },
      {
        id: 'monitorYear',
        header: <FormattedMessage id="monitorYear" />,
        accessorKey: 'monitorYear',
        width: 120
      },
      {
        id: 'monitoringStatus',
        header: <FormattedMessage id="monitoringStatus" />,
        accessorKey: 'monitoringStatus',
        width: 150,
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'stateEvaluation',
        header: <FormattedMessage id="stateEvaluation" />,
        accessorKey: 'stateEvaluation',
        width: 160
      },
      {
        id: 'pressureEvaluation',
        header: <FormattedMessage id="pressureEvaluation" />,
        accessorKey: 'pressureEvaluation',
        width: 170
      },
      {
        id: 'responseEvaluation',
        header: <FormattedMessage id="responseEvaluation" />,
        accessorKey: 'responseEvaluation',
        width: 180
      },
      {
        id: 'stateBest',
        header: <FormattedMessage id="stateBest" />,
        accessorKey: 'stateBest',
        width: 130
      },
      {
        id: 'stateWorst',
        header: <FormattedMessage id="stateWorst" />,
        accessorKey: 'stateWorst',
        width: 130
      },
      {
        id: 'stateMedian',
        header: <FormattedMessage id="stateMedian" />,
        accessorKey: 'stateMedian',
        width: 130
      },
      {
        id: 'numberTriggers',
        header: <FormattedMessage id="numberTriggers" />,
        accessorKey: 'numberTriggers',
        width: 130
      },
      {
        id: 'pressureBest',
        header: <FormattedMessage id="pressureBest" />,
        accessorKey: 'pressureBest',
        width: 130
      },
      {
        id: 'pressureWorst',
        header: <FormattedMessage id="pressureWorst" />,
        accessorKey: 'pressureWorst',
        width: 130
      },
      {
        id: 'pressureMedian',
        header: <FormattedMessage id="pressureMedian" />,
        accessorKey: 'pressureMedian',
        width: 150
      },
      {
        id: 'pressureTotal',
        header: <FormattedMessage id="pressureTotal" />,
        accessorKey: 'pressureTotal',
        width: 150
      },
      {
        id: 'coveragePa',
        header: <FormattedMessage id="coveragePa" />,
        accessorKey: 'coveragePa',
        width: 150
      },
      {
        id: 'coverageOecm',
        header: <FormattedMessage id="coverageOecm" />,
        accessorKey: 'coverageOecm',
        width: 150
      },
      {
        id: 'coveragePaOecm',
        header: <FormattedMessage id="coveragePaOecm" />,
        accessorKey: 'coveragePaOecm',
        width: 180
      },
      {
        id: 'managementPlanning',
        header: <FormattedMessage id="managementPlanning" />,
        accessorKey: 'managementPlanning',
        width: 160
      },
      {
        id: 'conservationAction',
        header: <FormattedMessage id="conservationAction" />,
        accessorKey: 'conservationAction',
        width: 160
      },
      // {
      //   id: 'actionsNeededUnderwayCount',
      //   header: <FormattedMessage id="Actions needed" />,
      //   accessorKey: 'actionsNeededUnderwayCount',
      //   width: 150
      // },
      {
        id: 'actionsNeededCount',
        header: <FormattedMessage id="actionsNeededCount" />,
        accessorKey: 'actionsNeededCount',
        width: 150
      },
      {
        id: 'actionsUnderway',
        header: <FormattedMessage id="actionsUnderway" />,
        accessorKey: 'actionsUnderway',
        width: 150
      }
    ]
}
